import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import useApi from "../../hooks/useApi";
import ProductionOtherCostModal from "./ProductionOtherCostModal";
import Modal from "../common/CommonModal";
import ProductListing from "../salesInvoice/ProductsModal";
const AssembleDismantleGeneral = ({
  productionData,
  setPurchaseInvoiceData,
  BASE_URL,
  COMPANY_CODE,
  DEPARTMENT,
  unitCostManual,
  setUnitCostManual,
  otherCosts,
  setOtherCosts,
  mode,
  isOtherCostModalOpen,
  setIsOtherCostModalOpen,
  handleProductSelect,
  isStockManagementOpen,
  setIsStockManagementOpen,
  netamount,
  currency,
  currencyRate,
}) => {
  const { request, loading } = useApi();

  const [glSettings, setGlSettings] = useState([]);
  const [productionType, setProductionType] = useState("ASSEMBLING");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchBy, setSearchBy] = useState("UNIVERSAL");
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  const fetchGLSettings = useCallback(async () => {
    const url = `${BASE_URL}/api/CRMGLSettings/${COMPANY_CODE}/SCRN_CODE/IN/-/${DEPARTMENT}/-`;
    try {
      const response = await request({ url, method: "GET", timeout: 10000 });
      setGlSettings(response || []);
    } catch (error) {
      console.error("Failed to fetch GL settings", error);
      setGlSettings([]);
    }
  }, [BASE_URL, COMPANY_CODE]);

  useEffect(() => {
    if (mode === "edit") return;
    fetchGLSettings();
  }, [fetchGLSettings, mode]);

  const getINAccounts = (gls) => {
    const findAcc = (code) =>
      gls.find((x) => x.Scrn_code === code)?.ACCOUNT?.trim() || "########";

    const stockAccount = findAcc("IN-STK-C");

    return {
      debitAcc: stockAccount,
      creditAcc: stockAccount,
    };
  };

  useEffect(() => {
    if (!glSettings.length) return;
    if (mode === "edit") return;

    const { debitAcc, creditAcc } = getINAccounts(glSettings);

    setPurchaseInvoiceData((prev) => ({
      ...prev,
      drAccount: debitAcc,
      crAccount: creditAcc,
    }));
  }, [glSettings, setPurchaseInvoiceData, mode]);

  // Handle F2 key press on product code field
  const handleProductCodeKeyDown = useCallback((e) => {
    if (e.key === "F2" || e.key === "f2") {
      e.preventDefault();
      setIsStockManagementOpen(true);
    }
  }, []);

  // Fetch products for the stock management modal
  const fetchProductData = useCallback(
    async (query, searchByValue, location = "A") => {
      const effectiveQuery = query?.trim() === "" ? "-" : query?.trim() || "-";
      const formattedQuery = effectiveQuery.replace(/\//g, "%2F");

      try {
        setProductsLoading(true);
        const url = `${BASE_URL}/api/CRMStockList/${COMPANY_CODE}/${searchByValue}/${formattedQuery}/${location}/-`;
        const response = await request({
          url: url,
          method: "GET",
          timeout: 5000,
        });
        if (response) {
          setProducts(response);
        } else {
          setProducts([]);
        }
      } catch (err) {
        console.error("Failed to fetch product data", err);
        setProducts([]);
      } finally {
        setProductsLoading(false);
      }
    },
    [BASE_URL, COMPANY_CODE, request],
  );

  // Fetch products when search query or search type changes
  useEffect(() => {
    fetchProductData(searchQuery, searchBy);
  }, [searchQuery, searchBy]);

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-4">
        {/* Section 1 - Production Info */}
        <div className="lg:col-span-4 col-span-1 flex flex-col gap-4">
          <FormControl className=" ">
            <RadioGroup
              row
              name="productionType"
              value={productionType}
              onChange={(e) => setProductionType(e.target.value)}
            >
              <FormControlLabel
                value="ASSEMBLING"
                control={<Radio sx={{ color: "#2563EB" }} />}
                label="Assembling"
              />

              <FormControlLabel
                value="DISMANTLING"
                control={<Radio sx={{ color: "#DC2626" }} />}
                label="Dismantling"
              />
            </RadioGroup>
          </FormControl>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-sm text-gray-600">Production No:</span>
              <span className="text-red-500 font-semibold">
                {productionData?.documentNo}
              </span>
            </div>
            <TextField
              label="Date"
              type="date"
              variant="outlined"
              size="small"
              value={productionData?.date}
              onChange={(e) =>
                setPurchaseInvoiceData((prev) => ({ ...prev, date: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </div>
          <div className="flex items-center gap-4">
            <TextField
              label="Estimation Number"
              variant="outlined"
              size="small"
              value={productionData?.estimationNumber}
              fullWidth
            />

            <TextField
              label="WO No"
              variant="outlined"
              size="small"
              value={productionData?.woNo}
              onChange={(e) =>
                setPurchaseInvoiceData((prev) => ({
                  ...prev,
                  woNo: e.target.value,
                }))
              }
              // InputProps={{
              //   readOnly: true,
              // }}
              fullWidth
            />
          </div>
          <div className="p-3 bg-gray-100 flex justify-between rounded-lg">
            <div>
              <span className="text-sm text-gray-600">Credit A/C:</span>{" "}
              <span className="font-bold text-orange-600">
                {productionData?.crAccount || "######"}
              </span>
            </div>
            <div>
              <span className="text-sm text-gray-600">Debit A/C:</span>
              <span className="font-bold text-green-600">
                {productionData?.drAccount || "#####"}
              </span>
            </div>
          </div>
        </div>

        {/* Section 2 - Estimation & WO */}
        <div className="lg:col-span-4 col-span-1 flex flex-col gap-4">
          <Autocomplete
            options={products}
            getOptionLabel={(option) =>
              option.Code || option.productCode || option.code || ""
            }
            value={
              products.find(
                (p) =>
                  (p.Code || p.productCode || p.code) ===
                  productionData?.productCode,
              ) || null
            }
            onChange={(event, newValue) => {
              if (newValue) {
                handleProductSelect(newValue);
              }
            }}
            onInputChange={(event, value) => {
              setSearchQuery(value);
            }}
            inputValue={searchQuery}
            loading={productsLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Product Code"
                placeholder="Type or press F2 for stock list"
                size="small"
                onKeyDown={handleProductCodeKeyDown}
                title="Type to search or Press F2 to select from list"
              />
            )}
            noOptionsText={
              productsLoading ? "Loading products..." : "No products found"
            }
            filterOptions={(options) => options}
            freeSolo={true}
            fullWidth
          />

          <TextField
            label="Product Description"
            variant="outlined"
            size="small"
            value={productionData?.productDescription}
            onChange={(e) =>
              setPurchaseInvoiceData((prev) => ({
                ...prev,
                productDescription: e.target.value,
              }))
            }
            fullWidth
            multiline
            rows={2}
            InputProps={{
              readOnly: true,
            }}
          />
        </div>

        {/* Section 3 - Production Details & Tracking */}
        <div className="lg:col-span-4 col-span-1 flex flex-col gap-4">
          <div className="flex gap-4">
            <TextField
              label="Production Qty"
              variant="outlined"
              size="small"
              type="number"
              value={productionData?.productionQty}
              onChange={(e) => {
                const val = Number(e.target.value || 0);
                setPurchaseInvoiceData((prev) => ({
                  ...prev,
                  productionQty: val,
                }));
              }}
              fullWidth
              InputProps={{
                inputProps: { step: "0.01" },
              }}
            />
            <TextField
              label="Unit Cost"
              variant="outlined"
              size="small"
              type="number"
              value={productionData?.unitCost ?? ""}
              onChange={(e) => {
                const val = Number(e.target.value || 0);
                setPurchaseInvoiceData((prev) => ({ ...prev, unitCost: val }));
              }}
              fullWidth
              InputProps={{
                readOnly: true,
              }}
            />
          </div>
          <div className="flex gap-4">
            <TextField
              label="Track History Code"
              variant="outlined"
              size="small"
              value={productionData?.trackHistoryCode}
              fullWidth
            />

            <TextField
              label="Design Code"
              variant="outlined"
              size="small"
              value={productionData?.trackId}
              fullWidth
            />
          </div>
          <div className="gap-4 grid grid-cols-2">
            <TextField
              label="Other Costs"
              variant="outlined"
              size="small"
              value={productionData?.otherCostsTotal ?? "0.00"}
              fullWidth
              InputProps={{ readOnly: true }}
            />
            <button
              onClick={() => setIsOtherCostModalOpen(true)}
              className="p-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-bold transition-colors flex items-center justify-center gap-2"
            >
              <span>+</span> Add Oth. Cost
            </button>
          </div>

          {/* HIGHLIGHTED NET AMOUNT CARD */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-3 shadow-sm">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-slate-500 text-sm font-bold">
                  Net Amount Due
                </h3>
              </div>
              <div className="text-right">
                <span className="text-3xl font-black text-slate-900">
                  {parseFloat(netamount || 0).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>
        <ProductionOtherCostModal
          open={isOtherCostModalOpen}
          onClose={() => setIsOtherCostModalOpen(false)}
          otherCosts={otherCosts}
          setOtherCosts={setOtherCosts}
          BASE_URL={BASE_URL}
          COMPANY_CODE={COMPANY_CODE}
          DEPARTMENT={DEPARTMENT}
          invNo={productionData?.documentNo}
          pdate={productionData?.date}
          jobNo={productionData?.jobCode}
          currency={currency}
          currencyRate={currencyRate}
        />
        <Modal
          isOpen={isStockManagementOpen}
          onClose={() => setIsStockManagementOpen(false)}
          title="Select Product"
          size="xxl"
          header={true}
        >
          <ProductListing
            modalSuggestions={products}
            loading={productsLoading}
            searchBy={searchBy}
            setSearchBy={setSearchBy}
            onSearchQueryChange={setSearchQuery}
            handleProductSelect={handleProductSelect}
          />
        </Modal>
      </div>
    </>
  );
};

export default AssembleDismantleGeneral;
